import React from 'react';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import { NativeFilterSelect } from './NativeFilterSelect';
import { AsyncModelSearchSelect } from './AsyncModelSearchSelect';
import { CodiceOemAutocomplete } from './CodiceOemAutocomplete';
import { FacetCategory, FacetsResponse, Origine, ORIGINE_PREDEFINITA_LABEL } from '../../hooks/useProductFacets';

interface ModelloProps {
  id: string;
  label: string;
  onChange: (id: string, label: string) => void;
}

interface Props {
  marca: string;
  onMarcaChange: (v: string) => void;
  tipologia: string;
  onTipologiaChange: (v: string) => void;
  origine: Origine;
  onOrigineChange: (v: Origine) => void;
  facets: FacetsResponse;
  loadingFacets: boolean;
  origineOptions: Array<{ value: 'compatibile'; label: string }>;
  /** Opzioni tipologia già "sanate" da `useProductFacets` (include il
   * gruppo merceologico attivo se arrivato dalla Ricerca Avanzata e non
   * presente tra i sottogruppi). Se assente, fallback a `facets.categories`
   * per compatibilità con consumer che non la passano ancora. */
  tipologiaOptions?: FacetCategory[];
  /** Se presente, mostra il selettore modello (async, ricerca server-side). */
  modello?: ModelloProps;
  /** Se presente, mostra l'input testuale Cod. DTK/OEM. */
  codice?: { value: string; onChange: (v: string) => void };
  /** Se assente, il campo Cerca resta nascosto (filtri applicati live, es. Outlet). Può ricevere un codice override (selezione da autocompletamento) per evitare stato stale. */
  onSearch?: (codiceOverride?: string) => void;
  onReset: () => void;
  isDirty: boolean;
  resultCount?: number;
  testidPrefix?: string;
}

/**
 * Barra filtri orizzontale condivisa da tutte le pagine di ricerca prodotti
 * (ricerca unificata categoria/modello, Outlet). Stessi campi del widget
 * "Ricerca avanzata" della home, ma in layout orizzontale.
 */
export function SearchFilterBar({
  marca, onMarcaChange,
  tipologia, onTipologiaChange,
  origine, onOrigineChange,
  facets, loadingFacets, origineOptions, tipologiaOptions,
  modello, codice,
  onSearch, onReset, isDirty,
  resultCount,
  testidPrefix = 'search-filter',
}: Props) {
  return (
    <div className="rounded-xl border bg-white shadow-sm p-3 mb-4" data-testid={`${testidPrefix}-bar`}>
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-full sm:w-[160px]">
          <NativeFilterSelect
            value={marca}
            onChange={onMarcaChange}
            disabled={loadingFacets}
            testid={`${testidPrefix}-marca`}
            placeholder="Tutte le marche"
            size="sm"
            options={facets.brands.map(b => ({ value: b, label: b }))}
          />
        </div>

        <div className="w-full sm:w-[230px]">
          <NativeFilterSelect
            value={tipologia}
            onChange={onTipologiaChange}
            disabled={loadingFacets}
            testid={`${testidPrefix}-tipologia`}
            placeholder="Tutte le tipologie"
            size="sm"
            options={(tipologiaOptions ?? facets.categories).map(c => ({ value: c.slug, label: c.label }))}
          />
        </div>

        <div className="w-full sm:w-[220px]">
          <NativeFilterSelect
            value={origine}
            onChange={v => onOrigineChange(v as Origine)}
            testid={`${testidPrefix}-origine`}
            placeholder={ORIGINE_PREDEFINITA_LABEL}
            size="sm"
            options={origineOptions}
          />
        </div>

        {modello && (
          <div className="w-full sm:flex-1 sm:min-w-[140px]">
            <AsyncModelSearchSelect
              value={modello.id}
              valueLabel={modello.label}
              onChange={modello.onChange}
              marca={marca || undefined}
              testid={`${testidPrefix}-modello`}
              placeholder="Modello"
              searchPlaceholder={marca ? `Cerca modello ${marca}...` : 'Cerca modello (min. 2 caratteri)...'}
              emptyMessage={marca ? `Nessun modello ${marca}` : 'Nessun modello trovato'}
              size="sm"
            />
          </div>
        )}

        {codice && (
          <div className="w-full sm:w-[150px]">
            <CodiceOemAutocomplete
              value={codice.value}
              onChange={codice.onChange}
              onSelect={code => onSearch?.(code)}
              onSubmit={() => onSearch?.()}
              placeholder="Cod. DTK/OEM"
              size="sm"
              testid={`${testidPrefix}-codice-input`}
            />
          </div>
        )}

        <div className="flex items-center gap-1.5 ml-auto w-full sm:w-auto">
          {loadingFacets && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
          {typeof resultCount === 'number' && (
            <span className="text-xs text-muted-foreground whitespace-nowrap mr-1" data-testid={`${testidPrefix}-result-count`}>
              {resultCount} prodotti
            </span>
          )}
          <button
            type="button"
            onClick={onReset}
            disabled={!isDirty}
            className="h-9 px-3 rounded-lg border bg-white text-xs font-medium hover:bg-secondary/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
            data-testid={`${testidPrefix}-azzera`}
          >
            <RotateCcw className="w-3 h-3" /> Azzera
          </button>
          {onSearch && (
            <button
              type="button"
              onClick={() => onSearch()}
              className="h-9 px-3.5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-xs font-semibold hover:bg-[hsl(214,58%,46%)] transition-colors inline-flex items-center gap-1"
              data-testid={`${testidPrefix}-cerca`}
            >
              <Search className="w-3 h-3" /> Cerca
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
